import { useState } from 'react'
import { Link, Outlet, useMatches, useRouterState } from '@tanstack/react-router'
import { useLiveQuery } from 'dexie-react-hooks'
import { useTranslation } from 'react-i18next'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { formatLocal } from '../lib/time.ts'
import { Banner } from './components.tsx'
import { KioskBar } from '../kiosk/KioskBar.tsx'
import { useClubLogo } from '../club/branding.ts'
import { Emblem } from './Emblem.tsx'
import { BottomNav } from './BottomNav.tsx'
import { Picto } from './pictos.tsx'
import { isTabRoot, profileOf } from './profile.ts'
import { LiveShareCtx, useShareMyPosition } from '../data/livePosition.ts'
import { useServiceWorker } from './serviceWorker.ts'

export type BadgeState = 'auth' | 'offline' | 'syncing' | 'pending' | 'never' | 'sent'
/** What the badge says, in this order (M23 item 7, the badge's half): a sign-in needed, then "Hors ligne" the moment the radio is off (it wins over a
 *  cycle still in flight: the phone says the truth at once instead of "Synchronisation…" until that cycle fails), then the cycle, the count waiting,
 *  "not yet" before the first answer, and "all sent". Pure, so that the order is tested on its own. */
export function badgeState(st: { authRequired: boolean; online: boolean | null; phase: 'idle' | 'syncing'; lastSyncAt: number | null }, pending: number): BadgeState {
  if (st.authRequired) return 'auth'
  if (st.online === false) return 'offline'
  if (st.phase === 'syncing') return 'syncing'
  if (pending > 0) return 'pending'
  if (st.lastSyncAt === null && st.online !== true) return 'never'          // nothing confirmed yet: do not claim "all sent"
  return 'sent'
}

export function SyncBadge() {
  const { t } = useTranslation()
  const { db, site } = useClub()
  const st = useEngineState()
  const pending = useLiveQuery(() => db.outbox.where('status').anyOf(['queued', 'sending', 'transient', 'blocked']).count(), [db], 0)
  const attention = useLiveQuery(() => db.outbox.where('status').anyOf(['rejected', 'conflict', 'error']).count(), [db], 0)
  const state = badgeState(st, pending)
  const tone = attention > 0 ? 'bg-red-600' : state === 'offline' || state === 'auth' || state === 'pending' ? 'bg-amber-600' : state === 'never' ? 'bg-slate-700/80' : 'bg-white/15 ring-1 ring-white/50'
  // the full words from `sm:`; on a phone the short ones ("Envoyé", "Synchro…"; "2 actions en attente" already carries its count, so the pill that
  // repeated it is gone: M23 item 49); under 360 px "En attente" with the count as a pill, which is all that fits beside the club's name
  const long = { auth: t('auth.signIn'), offline: t('sync.offline'), syncing: t('sync.syncing'), pending: t('sync.badgePending', { count: pending }), never: t('sync.notYet'), sent: t('sync.allSent') }[state]
  const short = { auth: t('auth.signIn'), offline: t('sync.offline'), syncing: t('sync.syncingShort'), pending: t('sync.badgePending', { count: pending }), never: t('sync.notYet'), sent: t('sync.allSentShort') }[state]
  const narrow = state === 'pending' ? t('sync.pendingShort') : short
  return (
    <Link to="/sync" data-testid="sync-badge" data-state={state} className={`flex min-h-14 shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-3 text-[13px] font-semibold text-white sm:text-sm ${tone}`}
      title={st.lastSyncAt ? t('sync.lastSync', { when: formatLocal(st.lastSyncAt, site.timezone, 'HH:mm') }) : t('sync.lastSync', { when: t('sync.never') })}>
      <span aria-hidden className={`size-2.5 rounded-full bg-white ${state === 'syncing' ? 'animate-pulse' : ''}`} />
      <span className="min-[360px]:hidden" data-testid="badge-narrow">{narrow}</span>
      <span className="hidden min-[360px]:inline sm:hidden" data-testid="badge-short">{short}</span>
      <span className="hidden sm:inline" data-testid="badge-long">{long}</span>
      {state === 'pending' && <span className="rounded-full bg-white/25 px-1.5 min-[360px]:hidden" data-testid="pending-count">{pending}</span>}
    </Link>
  )
}

/** The storage warning (M23-0, docs/M23.md item 2). In a browser that has not installed the app this is the normal state for weeks, so the 120–190 px
 *  banner on every screen is now one line on Today and Réglages only, with the install hint behind a tap and a "Compris" that puts it away on this
 *  device for seven days (`localStorage`, a per-device convenience: blocked storage only means the banner shows). It cannot be put away on Today
 *  before the device's first sync (the first sign-in), so that the person meets it once whole; `install_required` (iOS, read-only) is unchanged. */
export const STORAGE_BANNER_DAYS = 7
export const storageBannerKey = (orgId: string): string => `gk.banner.storage.${orgId}`
export function storageBannerDismissed(orgId: string, now = Date.now()): boolean {
  try {
    const at = Number(localStorage.getItem(storageBannerKey(orgId)))
    return at > 0 && now - at >= 0 && now - at < STORAGE_BANNER_DAYS * 86_400_000
  } catch { return false }
}
export function dismissStorageBanner(orgId: string, now = Date.now()): void {
  try { localStorage.setItem(storageBannerKey(orgId), String(now)) } catch { /* storage blocked: the banner is back at the next load */ }
}
export function StorageBanner({ orgId, path, firstSignIn }: { orgId: string; path: string; firstSignIn: boolean }) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(() => storageBannerDismissed(orgId))
  const [open, setOpen] = useState(false)
  if (dismissed || (path !== '/' && path !== '/settings')) return null
  const canDismiss = !(path === '/' && firstSignIn)
  // one line of text and ONE row of 56-px controls (the hint, and "Compris"): about 120 px, where the old banner took up to 190 on every screen
  return (
    <Banner tone="warn">
      <div className="flex flex-col gap-2" data-testid="storage-banner">
        <p className="font-semibold">{t('platform.storageShort')}</p>
        <div className={`grid gap-2 ${canDismiss ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <button type="button" className="btn !px-3" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="storage-install-link">{t('platform.installLink')}</button>
          {canDismiss && (
            <button type="button" className="btn" onClick={() => { dismissStorageBanner(orgId); setDismissed(true) }} data-testid="storage-understood">{t('common.understood')}</button>
          )}
        </div>
        {open && (
          <div className="flex flex-col gap-2 pt-1" data-testid="storage-install-hint">
            <p>{t('platform.installBody')}</p>
            <p>{t('platform.installAndroid')}</p>
          </div>
        )}
      </div>
    </Banner>
  )
}

/** The club's logo in a white disc, or the app's emblem when the club has not set one. */
export function ClubMark({ size = 'size-11' }: { size?: string }) {
  const logo = useClubLogo()
  return logo
    ? <span className={`${size} inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-0.5 shadow-sm ring-2 ring-white/70`}><img src={logo} alt="" className="size-full object-contain" data-testid="club-logo" /></span>
    : <Emblem className={`${size} shrink-0 drop-shadow-sm`} />
}

/** The way back in the header (M23 item 1): on every screen that is not a tab root, a 56-px control to the parent the route declares
 *  (`staticData.parent` in router.tsx, `/` when it says nothing), with the route's own params filled in (`/assets/$assetId/check` → the machine). */
export function HeaderBack({ path }: { path: string }) {
  const { t } = useTranslation()
  const matches = useMatches()
  if (isTabRoot(path)) return null
  const leaf = matches[matches.length - 1]
  const sd = leaf?.staticData as { parent?: string; origin?: (search: Record<string, unknown>) => { to: string; params: Record<string, string> } | null } | undefined
  // a route may say where it came from through its search (the procedure page: the timer or the job it was opened from), else its parent
  const origin = sd?.origin?.((leaf?.search ?? {}) as Record<string, unknown>) ?? null
  const parent = origin?.to ?? sd?.parent ?? '/'
  return (
    <Link to={parent as never} params={(origin?.params ?? leaf?.params ?? {}) as never} className="-ml-2 flex size-14 shrink-0 items-center justify-center rounded-full active:bg-white/20" aria-label={t('common.back')} data-testid="header-back" data-parent={parent}>
      <Picto name="chevron" className="size-7 -scale-x-100" strokeWidth={2.2} />
    </Link>
  )
}

export function Layout() {
  const { t } = useTranslation()
  const { org, site, platform, kiosk } = useClub()
  const path = useRouterState({ select: (s) => s.location.pathname })
  const wide = path.startsWith('/map') || path === '/plan' || /^\/course\/[^/]+\/shape/.test(path)      // a map wants the room a list does not
  const width = wide ? 'max-w-6xl' : 'max-w-3xl'
  // one app, an interface per profile (M19): the tabs follow the person's roles at the site; the sync page is the header badge's, for everybody
  const profile = profileOf(site.roles, !!kiosk)
  // the ONE place a position is shared: while a timer of the signed-in person runs in a stretch THIS device started or resumed, on screen (the native
  // app: also with the screen off); never on a workstation
  const share = useShareMyPosition()
  const sw = useServiceWorker()
  const st = useEngineState()
  if (path.startsWith('/plan/display')) return <Outlet />          // a screen in the shed: no header, no navigation, nothing to tap
  return (
    <div className={`mx-auto flex min-h-dvh ${width} flex-col`}>
      {/* every control of the header is 56 px (the way back, the club's name to Today, the badge); the paddings are trimmed so the bar stays 72 px */}
      <header className="turf sticky top-0 z-10 flex print:hidden items-center gap-2 px-4 pb-1.5 pt-[calc(0.375rem_+_var(--gk-safe-top))] text-white shadow-md" data-testid="app-header">
        <HeaderBack path={path} />
        <Link to="/" className="flex min-h-14 min-w-0 flex-1 items-center gap-3" aria-label={t('nav.today')}>
          {/* on a phone a sub-page shows the way back where the logo was: the name keeps its line (the logo stays on the tab roots and from sm:) */}
          <span className={isTabRoot(path) ? 'contents' : 'hidden sm:contents'}><ClubMark /></span>
          <span className="min-w-0">
            {/* one line each (M23 item 49: the name wrapped to two lines and the site line was cut at 375); the site only where the club has several */}
            <span className="block truncate font-serif text-base font-semibold leading-tight tracking-wide" data-testid="header-org">{org.org_name}</span>
            {org.sites.length > 1 && site.name !== org.org_name && <span className="block truncate text-xs text-white/80" data-testid="header-site">{site.name}</span>}
          </span>
        </Link>
        <SyncBadge />
      </header>
      <KioskBar />
      <div className="flex flex-col gap-3 px-4 pt-3 print:hidden">
        {platform.reason === 'install_required' && <Banner tone="warn"><strong>{t('platform.installTitle')}.</strong> {t('platform.installBody')} {t('platform.readOnly')}</Banner>}
        {platform.reason === 'unsupported' && <Banner tone="error">{t('platform.unsupported')}</Banner>}
        {platform.warnings.includes('storage_not_persistent') && <StorageBanner key={org.org_id} orgId={org.org_id} path={path} firstSignIn={st.lastSyncAt === null} />}
        {sw.needRefresh && <button type="button" className="btn w-full" onClick={sw.update} data-testid="sw-update"><Picto name="sync" className="size-5" />{t('platform.updateAvailable')}</button>}
      </div>
      <main className="flex-1 px-4 pt-4 pb-[calc(7rem_+_var(--gk-safe-bottom))] print:p-0"><LiveShareCtx.Provider value={share}><Outlet /></LiveShareCtx.Provider></main>
      <BottomNav profile={profile} width={width} />
    </div>
  )
}
